import { useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useUnlinkedPandaDocSearchQuery,
  UnlinkedPandaDocSearchQuery,
} from 'core/graphql';

type UnlinkedEdge = NonNullable<
  NonNullable<UnlinkedPandaDocSearchQuery['pandadocDocuments']>['edges'][number]
>;
export type UnlinkedPandaDocRow = NonNullable<UnlinkedEdge['node']>;

interface IUnlinkedPandadocSearchHook {
  /** Debounced search results — empty until the user types something. */
  data: UnlinkedPandaDocRow[];
  totalCount: number;
  loading: boolean;
  error?: ApolloError;
  /** True once the (debounced) query actually ran for the current input. */
  searched: boolean;
}

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 3;

/**
 * Debounced global search over UNLINKED PandaDoc documents (no agreement /
 * teacher / provisioning) by name or signer email/name. Powers the manual
 * «Привязать другой документ» box on the student card — the escape hatch for
 * documents whose signer contact data doesn't match the ERP profile, so the
 * automatic candidates never surface them.
 */
export const useUnlinkedPandadocSearch = (
  search: string,
  pageSize = 20,
): IUnlinkedPandadocSearchHook => {
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  const active = debounced.length >= MIN_QUERY_LENGTH;
  const { data, loading, error } = useUnlinkedPandaDocSearchQuery({
    variables: { search: debounced, docType: 'student', first: pageSize },
    skip: !active,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const rows = useMemo(
    () =>
      (data?.pandadocDocuments?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is UnlinkedPandaDocRow => Boolean(node)),
    [data],
  );

  return {
    data: active ? rows : [],
    totalCount: active ? data?.pandadocDocuments?.totalCount ?? 0 : 0,
    loading: active && loading,
    error,
    searched: active && !loading && data !== undefined,
  };
};
